import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withComponentInputBinding,
    withInMemoryScrolling,
    withPreloading
} from '@angular/router';
import { provideFuse } from '@fuse';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { mockApiServices } from 'app/mock-api';
import { provideTransloco } from '@ngneat/transloco';
import { HttpClient } from '@angular/common/http';
import { TranslocoHttpLoader } from './core/transloco-loader';

export function getSpanishPaginatorIntl(): MatPaginatorIntl {
    const paginatorIntl = new MatPaginatorIntl();
    paginatorIntl.itemsPerPageLabel = 'Elementos por página:';
    paginatorIntl.nextPageLabel = 'Siguiente página';
    paginatorIntl.previousPageLabel = 'Página anterior';
    paginatorIntl.firstPageLabel = 'Primera página';
    paginatorIntl.lastPageLabel = 'Última página';
    paginatorIntl.getRangeLabel = (
        page: number,
        pageSize: number,
        length: number
    ) => {
        if (length === 0 || pageSize === 0) {
            return `0 de ${length}`;
        }
        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        const endIndex =
            startIndex < length
                ? Math.min(startIndex + pageSize, length)
                : startIndex + pageSize;
        return `${startIndex + 1} - ${endIndex} de ${length}`;
    };
    return paginatorIntl;
}
import { environment } from 'environments/environment';
import { SLS_LOGGER_CONFIG } from 'app/shared/services/serverless/sls-logger.service';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withComponentInputBinding(),
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Transloco Configuration
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en-US',
                        label: 'English'
                    },
                    {
                        id: 'es-MX',
                        label: 'Español'
                    }
                ],
                defaultLang: 'en-US',
                fallbackLang: 'en-US',
                reRenderOnLangChange: true,
                prodMode: true
            },
            loader: TranslocoHttpLoader
        }),

        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D'
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy'
                }
            }
        },
        // SLS Logger
        {
            provide: SLS_LOGGER_CONFIG,
            useValue: {
                baseUrl: environment.slsLoggerBaseUrl,
                apiKey: environment.slsLoggerApiKey
            }
        },
        // Fuse
        provideAuth(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices
            },
            fuse: {
                layout: 'modern',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px'
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default'
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand'
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal'
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose'
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple'
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber'
                    }
                ]
            }
        })
    ]
};
